import api from '.'

export const trials = {
  get (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}`, null, success, fail)
  },
  update (id, data, success, fail) {
    api.authenticatedRequest('PUT', `/trials/${id}`, data, success, fail)
  },
  delete (id, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}`, null, success, fail)
  },
  getLogs (id, page, userType, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/logs?page=${page}&userType=${userType}`, null, success, fail)
  },
  getLogsCsv (id, userType, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/logs?format=csv&limit=2000&userType=${userType}`, null, success, fail)
  },
  getCommLogs (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/logs/communication`, null, success, fail)
  },
  getUsers (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/users`, null, success, fail)
  },
  getConnectedServices (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/connections`, null, success, fail)
  },
  connectToService (id, service, scope, code, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/connections/${service}`, { scope, code }, success, fail)
  },
  disconnectFromService (id, service, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}/connections/${service}`, null, success, fail)
  },
  createDataType (id, data, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/dataTypes`, data, success, fail)
  },
  getDataTypes (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/dataTypes`, null, success, fail)
  },
  createStage (id, data, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/stages`, data, success, fail)
  },
  importStages (id, creationType, importData, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/stages?creationType=${creationType}`, importData, success, fail)
  },
  generateStages (id, prompt, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/stages?creationType=generateFromAI`, { prompt }, success, fail)
  },
  getJob (jobId, success, fail) {
    api.authenticatedRequest('GET', `/jobs/${jobId}`, null, success, fail)
  },
  getQuestionnaireLibrary (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/questionnaire-library`, null, success, fail)
  },
  importFromLibrary (id, questionnaireId, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/stages?creationType=stageLibrary`, { importData: { questionnaireId } }, success, fail)
  },
  getStages (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/stages`, null, success, fail)
  },
  createGroup (id, data, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/groups`, data, success, fail)
  },
  getGroups (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/groups`, null, success, fail)
  },
  createParticipant (id, data, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/participants`, data, success, fail)
  },
  getParticipants (id, page, term, site, unread, sort, sortOrder, filters, success, fail) {
    // Backwards compatibility: older calls don't pass `filters` (success is in that slot)
    if (typeof filters === 'function') {
      fail = success
      success = filters
      filters = {}
    }

    const params = new URLSearchParams({ page: page || 1, term: term || '', site: site || '', sort: sort || '', sortOrder: sortOrder || '' })
    if (unread) params.set('unread', unread)

    if (filters) {
      if (filters.showArchived) params.set('includeArchived', 'true')
      if (filters.scheduleBehind) params.set('scheduleBehind', 'true')
      if (filters.scheduleOnSchedule) params.set('scheduleOnSchedule', 'true')

      if (filters.allocationUnallocated) params.set('allocationUnallocated', 'true')
      if (filters.allocationAllocated) params.set('allocationAllocated', 'true')

      if (filters.joinedJoined) params.set('joinedJoined', 'true')
      if (filters.joinedNotJoined) params.set('joinedNotJoined', 'true')

      if (filters.groupIncludeIds?.length) params.set('groupsInclude', filters.groupIncludeIds.join(','))
      if (filters.groupExcludeIds?.length) params.set('groupsExclude', filters.groupExcludeIds.join(','))
      if (filters.groupIncludeNone) params.set('groupsIncludeNone', 'true')
      if (filters.groupExcludeNone) params.set('groupsExcludeNone', 'true')
    }

    api.authenticatedRequest('GET', `/trials/${id}/participants?${params}`, null, success, fail)
  },
  getParticipantsCsv (id, { includeArchived } = {}, success, fail) {
    const params = new URLSearchParams({ format: 'csv' })
    if (includeArchived) params.set('includeArchived', 'true')
    api.authenticatedRequest('GET', `/trials/${id}/participants?${params}`, null, success, fail)
  },
  getSavedFilters (id, type, success, fail) {
    const params = new URLSearchParams()
    if (type) params.set('type', type)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    api.authenticatedRequest('GET', `/trials/${id}/saved-filters${suffix}`, null, success, fail)
  },
  createSavedFilter (id, data, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/saved-filters`, data, success, fail)
  },
  updateSavedFilter (id, filterId, data, success, fail) {
    api.authenticatedRequest('PATCH', `/trials/${id}/saved-filters/${filterId}`, data, success, fail)
  },
  deleteSavedFilter (id, filterId, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}/saved-filters/${filterId}`, null, success, fail)
  },
  deleteParticipant (id, participantId, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}/participants/${participantId}`, null, success, fail)
  },
  updateParticipant (id, participantId, data, success, fail) {
    api.authenticatedRequest('PUT', `/trials/${id}/participants/${participantId}`, data, success, fail)
  },
  deleteParticipants (id, data, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}/participants`, data, success, fail)
  },
  bulkUpdateParticipants (id, updates, success, fail) {
    api.authenticatedRequest('PATCH', `/trials/${id}/participants`, { updates }, success, fail)
  },
  getParticipantMessages (id, participantId, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/participants/${participantId}/messages`, null, success, fail)
  },
  createParticipantMessage (id, participantId, data, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/participants/${participantId}/messages`, data, success, fail)
  },
  readParticipantMessages (id, participantId, success, fail) {
    api.authenticatedRequest('PUT', `/trials/${id}/participants/${participantId}/messages`, null, success, fail)
  },
  createParticipantNotifications (id, data, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/participants/notifications`, data, success, fail)
  },
  createConditionsAgreement (id, agreed, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/conditionAgreements`, { agreed }, success, fail)
  },
  createResponse (id, stageId, data, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/stages/${stageId}/responses`, data, success, fail)
  },
  getResponses (id, type, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/responses?type=${type || 'json'}`, null, success, fail)
  },
  getRawResponses (id, dataset, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/responses?type=raw&dataset=${dataset || 'stage'}`, null, success, fail)
  },
  createRawHardwareExportJob (id, success, fail) {
    // Kicks off an async job (the raw hardware dump can be GB-scale and would
    // time out a synchronous request). Returns `{ jobId }`; poll via getJob.
    api.authenticatedRequest('POST', `/trials/${id}/responses?type=raw&dataset=hardware`, null, success, fail)
  },
  getHardwareResponses (id, type, dataset, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/responses?type=${type || 'json'}&dataset=${dataset || 'core'}`, null, success, fail)
  },
  getFoodDiaryResponses (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/responses?type=csv&dataset=foodDiary`, null, success, fail)
  },
  getResponsesExplore (id, args, success, fail) {
    args.type = 'explore'
    const searchParams = new URLSearchParams(args)
    api.authenticatedRequest('GET', `/trials/${id}/responses?${searchParams}`, null, success, fail)
  },
  getStageResponses (id, stageId, type, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/stages/${stageId}/responses?type=${type || 'json'}`, null, success, fail)
  },
  getParticipantResponses (id, participantId, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/participants/${participantId}/responses`, null, success, fail)
  },
  getParticipantHardwareResponses (id, participantId, success, fail, dataset = null) {
    const url = dataset
      ? `/trials/${id}/participants/${participantId}/hardwareResponses?dataset=${dataset}`
      : `/trials/${id}/participants/${participantId}/hardwareResponses`
    api.authenticatedRequest('GET', url, null, success, fail)
  },
  allocateResponseToUnallocatedParticipant (id, responseId, success, fail, { participant } = {}) {
    const params = new URLSearchParams()
    if (participant) params.set('participant', participant)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    api.authenticatedRequest('POST', `/trials/${id}/responses/${responseId}/allocations${suffix}`, null, success, fail)
  },
  deallocateResponseFromParticipant (id, responseId, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}/responses/${responseId}/allocations`, null, success, fail)
  },
  setResponseStatus (id, responseId, status, success, fail) {
    api.authenticatedRequest('PUT', `/trials/${id}/responses/${responseId}/status`, { status: status || null }, success, fail)
  },
  updateParticipantResponse (id, participantId, stageId, data, success, fail) {
    api.authenticatedRequest('PUT', `/trials/${id}/participants/${participantId}/responses/${stageId}`, data, success, fail)
  },
  deleteResponse (id, responseId, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}/responses/${responseId}`, null, success, fail)
  },
  updateParticipantResponseDraft (id, participantId, stageId, data, success, fail) {
    api.authenticatedRequest('PUT', `/trials/${id}/participants/${participantId}/responses/${stageId}?draft=true`, data, success, fail)
  },
  createResponseValidation (id, responseId, dataTypeId, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/responses/${responseId}/dataTypes/${dataTypeId}/validations`, null, success, fail)
  },
  deleteResponseValidation (id, responseId, dataTypeId, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}/responses/${responseId}/dataTypes/${dataTypeId}/validations`, null, success, fail)
  },
  deleteParticipantResponseDraft (id, participantId, stageId, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}/participants/${participantId}/draftResponses/${stageId}`, null, success, fail)
  },
  createParticipantStageMagicLink (trialId, participantId, stageId, data, success, fail) {
    api.authenticatedRequest(
      'POST',
      `/trials/${trialId}/participants/${participantId}/stages/${stageId}/tokens`,
      data || {},
      success,
      fail
    )
  },
  getAutomaticMessages (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/automaticMessages`, null, success, fail)
  },
  createAutomaticMessage (id, data, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/automaticMessages`, data, success, fail)
  },
  deleteAutomaticMessage (id, messageId, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}/automaticMessages/${messageId}`, null, success, fail)
  },
  getConsentDocuments (id, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/consentDocuments`, null, success, fail)
  },
  createConsentDocument (id, data, success, fail) {
    api.authenticatedRequest('POST', `/trials/${id}/consentDocuments`, data, success, fail)
  },
  updateConsentDocument (id, documentId, data, success, fail) {
    api.authenticatedRequest('PATCH', `/trials/${id}/consentDocuments/${documentId}`, data, success, fail)
  },
  deleteConsentDocument (id, documentId, data, success, fail) {
    api.authenticatedRequest('DELETE', `/trials/${id}/consentDocuments/${documentId}`, data || null, success, fail)
  },
  getConsentDocumentPublic (trialIdOrCode, { documentId, formKey } = {}, success, fail) {
    const params = new URLSearchParams()
    if (documentId) params.set('documentId', documentId)
    if (formKey) params.set('formKey', formKey)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    api.unauthenticatedRequest('GET', `/trials/${trialIdOrCode}/consentDocument${suffix}`, null, success, fail)
  },
  submitConsent (id, documentId, data, success, fail) {
    api.unauthenticatedRequest('POST', `/trials/${id}/consentDocuments/${documentId}/consents`, data, success, fail)
  },
  listConsentsForTrial (id, params, success, fail) {
    const searchParams = new URLSearchParams(params)
    api.authenticatedRequest('GET', `/trials/${id}/consents?${searchParams}`, null, success, fail)
  },
  getPublicMeeting (id, password, success, fail) {
    api.unauthenticatedRequest('GET', `/participants/meetings/${id}?meetingPassword=${password}`, null, success, fail)
  },
  getWallet (trialId, { page = 1, perPage = 30, category } = {}, success, fail) {
    const params = new URLSearchParams({ page, perPage })
    if (category) params.set('category', category)
    api.authenticatedRequest('GET', `/trials/${trialId}/wallet/balance?${params}`, null, success, fail)
  },
  createTopupCheckout (trialId, amount, currency, success, fail) {
    api.authenticatedRequest('POST', `/trials/${trialId}/wallet/checkout`, { amount, currency }, success, fail)
  },
  adminAdjustWallet (trialId, amount, description, success, fail) {
    api.authenticatedRequest('POST', `/trials/${trialId}/wallet/transactions`, { amount, description }, success, fail)
  },
  search (id, q, success, fail) {
    api.authenticatedRequest('GET', `/trials/${id}/search?q=${encodeURIComponent(q)}`, null, success, fail)
  }
}
